import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useMutation } from 'react-query';
import type { TripDirection } from 'contracts/transport-routes';
import { useCityOptions, useUfOptions } from 'hooks/api/options';
import { useFetchCityByFieldAndValue } from 'hooks/api/cities';
import { useForwardTripState, useBackwardTripState } from 'pages/Settings/transport-routes/hooks';
import type { SelectContentProps } from 'components/ui/Forms';

import type { FormSchema } from '../form-schema';

type Options = {
  form: UseFormReturn<FormSchema>;
  tripDirection: TripDirection;
}

export const useTripManagerState = ({ tripDirection, form }: Options) => {
  const ufOptions = useUfOptions();
  const { fetchCities } = useCityOptions();
  const { fetchCityByFieldAndValue } = useFetchCityByFieldAndValue();
  const {
    forwardTripFormValues,
    forwardTripCoordinates,
    updateForwardTripFormValues,
    updateForwardTripCoordinates,
  } = useForwardTripState();
  const {
    updateBackwardTripFormValues,
    updateBackwardTripCoordinates,
  } = useBackwardTripState();

  const [originCityOptions, setOriginCityOptions]
    = React.useState<SelectContentProps[]>([]);
  const [destinationCityOptions, setDestinationCityOptions]
    = React.useState<SelectContentProps[]>([]);
  const lastOriginUfRequestRef = React.useRef<string | null>(null);
  const lastDestinationUfRequestRef = React.useRef<string | null>(null);
  const lastOriginIbgeLookupRef = React.useRef<string | null>(null);
  const lastDestinationIbgeLookupRef = React.useRef<string | null>(null);
  const lastSyncedBackwardOriginKeyRef = React.useRef<string | null>(null);
  const lastSyncedBackwardDestinationKeyRef = React.useRef<string | null>(null);

  const originCityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setOriginCityOptions(data.singleSelectOptions);
    },
  });

  const destinationCityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setDestinationCityOptions(data.singleSelectOptions);
    },
  });

  const originCityMutation = useMutation({
    mutationFn: fetchCityByFieldAndValue,
  });

  const destinationCityMutation = useMutation({
    mutationFn: fetchCityByFieldAndValue,
  });

  const requestOriginCityOptions = React.useCallback((uf?: string | null) => {
    if (!uf) return;
    if (lastOriginUfRequestRef.current === uf) return;

    lastOriginUfRequestRef.current = uf;
    originCityOptionsMutation.mutate(uf);
  }, [originCityOptionsMutation]);

  const requestDestinationCityOptions = React.useCallback((uf?: string | null) => {
    if (!uf) return;
    if (lastDestinationUfRequestRef.current === uf) return;

    lastDestinationUfRequestRef.current = uf;
    destinationCityOptionsMutation.mutate(uf);
  }, [destinationCityOptionsMutation]);

  const requestOriginCityByIbge = React.useCallback((ibge?: string | null, onSuccess?: (city: any) => void) => {
    if (!ibge) return;

    const normalizedIbge = String(ibge);
    if (lastOriginIbgeLookupRef.current === normalizedIbge) return;

    lastOriginIbgeLookupRef.current = normalizedIbge;
    originCityMutation.mutate(
      { field: 'ibge', value: normalizedIbge },
      { onSuccess },
    );
  }, [originCityMutation]);

  const requestDestinationCityByIbge = React.useCallback((ibge?: string | null, onSuccess?: (city: any) => void) => {
    if (!ibge) return;

    const normalizedIbge = String(ibge);
    if (lastDestinationIbgeLookupRef.current === normalizedIbge) return;

    lastDestinationIbgeLookupRef.current = normalizedIbge;
    destinationCityMutation.mutate(
      { field: 'ibge', value: normalizedIbge },
      { onSuccess },
    );
  }, [destinationCityMutation]);

  const onOriginUfChange = React.useCallback((uf: string, isLoadForm?: boolean) => {
    form.resetField('origin_ibge');
    form.resetField('origin_lat');
    form.resetField('origin_lng');
    requestOriginCityOptions(uf);

    if (tripDirection === 'forward') {
      if (!isLoadForm) {
        updateForwardTripFormValues({
          origin_uf: uf,
          origin_ibge: undefined,
        });
        updateForwardTripCoordinates({
          origin_lat: undefined,
          origin_lng: undefined,
        });
        updateBackwardTripCoordinates({
          destination_lat: undefined,
          destination_lng: undefined,
        });
      } else {
        updateForwardTripFormValues({
          origin_uf: uf,
        });
      }
    } else if (tripDirection === 'backward' && !isLoadForm) {
      updateBackwardTripFormValues({
        origin_uf: uf,
        origin_ibge: undefined,
      });
      updateBackwardTripCoordinates({
        origin_lat: undefined,
        origin_lng: undefined,
      });
    }
  }, [
    form,
    requestOriginCityOptions,
    tripDirection,
    updateForwardTripFormValues,
    updateBackwardTripFormValues,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onOriginIbgeChange = React.useCallback((ibge: string, preserveCurrentCoordinates?: boolean) => {
    if (tripDirection === 'forward') {
      requestOriginCityByIbge(ibge, (city) => {
        if (!preserveCurrentCoordinates) {
          updateForwardTripCoordinates({
            origin_lat: city?.latitude,
            origin_lng: city?.longitude,
          });
          form.setValue('origin_lat', city?.latitude ?? '');
          form.setValue('origin_lng', city?.longitude ?? '');
        }

        updateForwardTripFormValues({
          origin_ibge: ibge,
        });
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripFormValues({
        origin_ibge: ibge,
      });
      requestOriginCityByIbge(ibge, (city) => {
        if (!preserveCurrentCoordinates) {
          updateBackwardTripCoordinates({
            origin_lat: city?.latitude,
            origin_lng: city?.longitude,
          });
          form.setValue('origin_lat', city?.latitude ?? '');
          form.setValue('origin_lng', city?.longitude ?? '');
        }
      });
    }
  }, [
    form,
    tripDirection,
    requestOriginCityByIbge,
    updateForwardTripFormValues,
    updateBackwardTripFormValues,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onDestinationUfChange = React.useCallback((uf: string, isLoadForm?: boolean) => {
    form.resetField('destination_ibge');
    form.resetField('destination_lat');
    form.resetField('destination_lng');
    requestDestinationCityOptions(uf);

    if (tripDirection === 'forward') {
      if (!isLoadForm) {
        updateForwardTripFormValues({
          destination_uf: uf,
          destination_ibge: undefined,
        });
        updateForwardTripCoordinates({
          destination_lat: undefined,
          destination_lng: undefined,
        });
        updateBackwardTripCoordinates({
          origin_lat: undefined,
          origin_lng: undefined,
        });
      } else {
        updateForwardTripFormValues({
          destination_uf: uf,
        });
      }
    } else if (tripDirection === 'backward') {
      updateBackwardTripFormValues({
        destination_uf: uf,
        destination_ibge: undefined,
      });
      updateBackwardTripCoordinates({
        destination_lat: undefined,
        destination_lng: undefined,
      });
    }
  }, [
    form,
    requestDestinationCityOptions,
    tripDirection,
    updateForwardTripFormValues,
    updateBackwardTripFormValues,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onDestinationIbgeChange = React.useCallback((ibge: string, preserveCurrentCoordinates?: boolean) => {
    if (tripDirection === 'forward') {
      requestDestinationCityByIbge(ibge, (city) => {
        if (!preserveCurrentCoordinates) {
          updateForwardTripCoordinates({
            destination_lat: city?.latitude,
            destination_lng: city?.longitude,
          });
          form.setValue('destination_lat', city?.latitude ?? '');
          form.setValue('destination_lng', city?.longitude ?? '');
        }

        updateForwardTripFormValues({
          destination_ibge: ibge,
        });
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripFormValues({
        destination_ibge: ibge,
      });
      requestDestinationCityByIbge(ibge, (city) => {
        if (!preserveCurrentCoordinates) {
          updateBackwardTripCoordinates({
            destination_lat: city?.latitude,
            destination_lng: city?.longitude,
          });
          form.setValue('destination_lat', city?.latitude ?? '');
          form.setValue('destination_lng', city?.longitude ?? '');
        }
      });
    }
  }, [
    form,
    tripDirection,
    requestDestinationCityByIbge,
    updateForwardTripFormValues,
    updateBackwardTripFormValues,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onOriginLatChange = React.useCallback((originLat: string) => {
    if (tripDirection === 'forward') {
      updateForwardTripCoordinates({
        origin_lat: originLat,
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripCoordinates({
        origin_lat: originLat,
      });
    }
  }, [
    tripDirection,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onOriginLngChange = React.useCallback((originLng: string) => {
    if (tripDirection === 'forward') {
      updateForwardTripCoordinates({
        origin_lng: originLng,
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripCoordinates({
        origin_lng: originLng,
      });
    }
  }, [
    tripDirection,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onDestinationLatChange = React.useCallback((destinationLat: string) => {
    if (tripDirection === 'forward') {
      updateForwardTripCoordinates({
        destination_lat: destinationLat,
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripCoordinates({
        destination_lat: destinationLat,
      });
    }
  }, [
    tripDirection,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const onDestinationLngChange = React.useCallback((destinationLng: string) => {
    if (tripDirection === 'forward') {
      updateForwardTripCoordinates({
        destination_lng: destinationLng,
      });
    } else if (tripDirection === 'backward') {
      updateBackwardTripCoordinates({
        destination_lng: destinationLng,
      });
    }
  }, [
    tripDirection,
    updateForwardTripCoordinates,
    updateBackwardTripCoordinates,
  ]);

  const triggerUpdateBackwardTripOriginLocation = React.useCallback(() => {
    if (tripDirection !== 'backward') return;

    const { destination_uf, destination_ibge } = forwardTripFormValues;
    if (!destination_uf || !destination_ibge) return;
    const { destination_lat, destination_lng } = forwardTripCoordinates;

    const forwardOriginKey = [
      destination_uf,
      destination_ibge,
      destination_lat,
      destination_lng,
    ].join('-');
    if (lastSyncedBackwardOriginKeyRef.current === forwardOriginKey) {
      return;
    }
    lastSyncedBackwardOriginKeyRef.current = forwardOriginKey;

    const currentOriginUf = form.getValues('origin_uf');
    const currentOriginIbge = form.getValues('origin_ibge');
    const shouldUpdateOriginUf = currentOriginUf !== destination_uf;
    const shouldUpdateOriginIbge = currentOriginIbge !== destination_ibge;

    requestOriginCityOptions(destination_uf);

    if (shouldUpdateOriginUf) {
      form.setValue('origin_uf', destination_uf);
    }

    if (shouldUpdateOriginIbge) {
      form.setValue('origin_ibge', destination_ibge);
    }

    updateBackwardTripFormValues({
      origin_uf: destination_uf,
      origin_ibge: destination_ibge,
    });

    if (destination_lat && destination_lng) {
      updateBackwardTripCoordinates({
        origin_lat: destination_lat,
        origin_lng: destination_lng,
      });
      form.setValue('origin_lat', destination_lat);
      form.setValue('origin_lng', destination_lng);
      return;
    }

    requestOriginCityByIbge(destination_ibge, (city) => {
      updateBackwardTripCoordinates({
        origin_lat: city?.latitude,
        origin_lng: city?.longitude,
      });
      form.setValue('origin_lat', city?.latitude ?? '');
      form.setValue('origin_lng', city?.longitude ?? '');
    });
  }, [
    tripDirection,
    form,
    forwardTripFormValues,
    forwardTripCoordinates,
    requestOriginCityOptions,
    requestOriginCityByIbge,
    updateBackwardTripCoordinates,
    updateBackwardTripFormValues,
  ]);

  const triggerUpdateBackwardTripDestinationLocation = React.useCallback(() => {
    if (tripDirection !== 'backward') return;
    const { origin_uf, origin_ibge } = forwardTripFormValues;
    if (!origin_uf || !origin_ibge) return;
    const { origin_lat, origin_lng } = forwardTripCoordinates;

    const forwardDestinationKey = [
      origin_uf,
      origin_ibge,
      origin_lat,
      origin_lng,
    ].join('-');
    if (lastSyncedBackwardDestinationKeyRef.current === forwardDestinationKey) {
      return;
    }
    lastSyncedBackwardDestinationKeyRef.current = forwardDestinationKey;

    const currentDestinationUf = form.getValues('destination_uf');
    const currentDestinationIbge = form.getValues('destination_ibge');
    const shouldUpdateDestinationUf = currentDestinationUf !== origin_uf;
    const shouldUpdateDestinationIbge = currentDestinationIbge !== origin_ibge;

    requestDestinationCityOptions(origin_uf);

    if (shouldUpdateDestinationUf) {
      form.setValue('destination_uf', origin_uf);
    }

    if (shouldUpdateDestinationIbge) {
      form.setValue('destination_ibge', origin_ibge);
    }

    updateBackwardTripFormValues({
      destination_uf: origin_uf,
      destination_ibge: origin_ibge,
    });

    if (origin_lat && origin_lng) {
      updateBackwardTripCoordinates({
        destination_lat: origin_lat,
        destination_lng: origin_lng,
      });
      form.setValue('destination_lat', origin_lat);
      form.setValue('destination_lng', origin_lng);
      return;
    }

    requestDestinationCityByIbge(origin_ibge, (city) => {
      updateBackwardTripCoordinates({
        destination_lat: city?.latitude,
        destination_lng: city?.longitude,
      });
      form.setValue('destination_lat', city?.latitude ?? '');
      form.setValue('destination_lng', city?.longitude ?? '');
    });
  }, [
    tripDirection,
    form,
    forwardTripFormValues,
    forwardTripCoordinates,
    requestDestinationCityOptions,
    requestDestinationCityByIbge,
    updateBackwardTripCoordinates,
    updateBackwardTripFormValues,
  ]);

  React.useEffect(() => {
    triggerUpdateBackwardTripOriginLocation();
  }, [triggerUpdateBackwardTripOriginLocation]);

  React.useEffect(() => {
    triggerUpdateBackwardTripDestinationLocation();
  }, [triggerUpdateBackwardTripDestinationLocation]);

  return {
    originCityOptions,
    destinationCityOptions,
    ufOptions,
    onOriginUfChange,
    onOriginIbgeChange,
    onDestinationUfChange,
    onDestinationIbgeChange,
    onOriginLatChange,
    onOriginLngChange,
    onDestinationLatChange,
    onDestinationLngChange,
  };
};
